import Phaser from 'phaser';
import type { TerrainHex, TerrainType, OverlayType, SpecialHex, HexCoord, LogEntry } from '~shared/types';
import { getTerrainSummary } from '../utils/terrainSummary';
import { drawIronCross } from './MedalTrack';

export const HEX_SIZE = 40;
const H_SPACING = Math.sqrt(3) * HEX_SIZE;  // ~69.3
const V_SPACING = HEX_SIZE * 1.5;           // 60

export const BOARD_ROWS = 9;
export function maxColInRow(row: number): number {
  return row % 2 === 0 ? 12 : 11;
}
export function isValidHex(col: number, row: number): boolean {
  return row >= 0 && row < BOARD_ROWS && col >= 0 && col <= maxColInRow(row);
}

const TERRAIN_COLORS: Record<TerrainType, number> = {
  clear:         0x92aa48,  // grassland base
  forest:        0x3e5818,  // dark-grass forest floor
  hill:              0x92aa48,  // same grassland base as clear
  'impassable-hill': 0x92aa48,  // same base; red X drawn on top
  bluff:         0xa89060,  // sandy-brown cliff face
  town:          0x6e6860,  // mortar/gap colour
  river:         0x92aa48,  // grassland base — river band drawn on top
  bridge:        0x92aa48,  // river base rendered first; bridge drawn on top
  beach:         0xe8d48a,
  hedgerow:      0x7a9848,  // mid-grass base
  pond:          0x1a44aa,
  ocean:         0x1a4a99,  // deep sea blue
  'frozen-lake': 0xc8e4f4,  // ice blue-white base
  ravine:            0x92aa48,  // grassland base — gully drawn on top
  trench:            0x92aa48,  // grassland base — dug trench drawn on top
  'city-ruins':      0x5c5244,  // dark rubble-brown base for contrast against stone chips
  'hill-forest':     0x92aa48,  // same grassland base as hill
  'hill-village':    0x92aa48,  // same grassland base as hill
  'factory-complex': 0x4a4840,  // dark concrete base for contrast against yard texture
  marsh:             0x5c6a3e,  // muddy olive-green base
};

// Theme-specific overrides for clear hex base colour
const CLEAR_THEME_COLORS: Record<string, number> = {
  winter: 0xdce8f0,  // matte blue-white snow
  desert: 0xd8b870,  // sandy tan
};

const FOREST_THEME_COLORS: Record<string, number> = {
  winter: 0xc8d4de,  // slightly greyer blue-white (snow under dark conifers)
};

// Overlay types that override the hex fill colour (others use the base terrain colour).
const OVERLAY_FILL: Partial<Record<OverlayType, number>> = {
  // bunker no longer floods the hex — terrain shows through at the edges
};

export function hexToPixel(
  col: number, row: number, offsetX: number, offsetY: number,
): { x: number; y: number } {
  return {
    x: offsetX + H_SPACING * col + (row % 2) * (H_SPACING / 2),
    y: offsetY + V_SPACING * row,
  };
}

function pixelToHex(px: number, py: number, offsetX: number, offsetY: number): HexCoord | null {
  let bestDist = Infinity;
  let best: HexCoord | null = null;
  for (let row = 0; row < BOARD_ROWS; row++) {
    for (let col = 0; col <= maxColInRow(row); col++) {
      const { x, y } = hexToPixel(col, row, offsetX, offsetY);
      const d = (px - x) ** 2 + (py - y) ** 2;
      if (d < bestDist) { bestDist = d; best = { col, row }; }
    }
  }
  if (best && bestDist < (HEX_SIZE * 0.95) ** 2) return best;
  return null;
}

function hexPoly(cx: number, cy: number, size: number): Phaser.Geom.Point[] {
  const pts: Phaser.Geom.Point[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 2; // pointy-top
    pts.push(new Phaser.Geom.Point(cx + size * Math.cos(a), cy + size * Math.sin(a)));
  }
  return pts;
}

// ── Terrain detail drawing ────────────────────────────────────────────────────

// Deterministic per-hex RNG (LCG seeded by col/row) so every clear hex
// looks different but never shifts between renders.
function makeRng(col: number, row: number) {
  let s = (col * 1234567 + row * 7654321 + 99) | 0;
  return () => {
    s = Math.imul(s, 1664525) + 1013904223;
    return ((s >>> 0) / 0x100000000);
  };
}

// Jagged line helper for ice cracks — wiggles between endpoints using RNG.
function drawJaggedLine(
  g: Phaser.GameObjects.Graphics,
  x1: number, y1: number, x2: number, y2: number,
  steps: number, wobble: number, rng: () => number,
): void {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 0.5) return;
  const nx = -dy / len, ny = dx / len;  // perpendicular unit vector
  const pts: { x: number; y: number }[] = [{ x: x1, y: y1 }];
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const offset = (rng() - 0.5) * wobble;
    pts.push({ x: x1 + dx * t + nx * offset, y: y1 + dy * t + ny * offset });
  }
  pts.push({ x: x2, y: y2 });
  g.strokePoints(pts, false);
}

// Frozen lake: full ice-blue coverage + deterministic crack pattern per hex.
function drawFrozenLakeDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col, row);
  const r   = HEX_SIZE - 2;

  // Sheen — lighter reflection ellipse near upper-left
  g.fillStyle(0xeef6fc, 0.55);
  g.fillEllipse(cx - r * 0.18, cy - r * 0.28, r * 0.80, r * 0.42);

  // Secondary diffuse highlight
  g.fillStyle(0xf8fcff, 0.22);
  g.fillEllipse(cx + r * 0.10, cy - r * 0.05, r * 0.45, r * 0.28);

  // Crack origin — slightly off-centre for a natural feel
  const ox = cx + (rng() - 0.5) * 12;
  const oy = cy + (rng() - 0.5) * 12;

  const numCracks = 2 + Math.floor(rng() * 2);   // 2–3 main cracks

  for (let i = 0; i < numCracks; i++) {
    const angle = rng() * Math.PI * 2;
    const ex    = cx + r * 0.92 * Math.cos(angle);
    const ey    = cy + r * 0.92 * Math.sin(angle);

    // Main crack
    g.lineStyle(1.2, 0x88b0cc, 0.88);
    drawJaggedLine(g, ox, oy, ex, ey, 3 + Math.floor(rng() * 3), 6 + rng() * 5, rng);

    // Branch off the main crack
    const branchT   = 0.30 + rng() * 0.40;
    const bx        = ox + (ex - ox) * branchT;
    const by_       = oy + (ey - oy) * branchT;
    const branchAng = angle + (rng() > 0.5 ? 1 : -1) * (0.4 + rng() * 0.8);
    const branchLen = r * (0.18 + rng() * 0.22);
    const bex       = bx + branchLen * Math.cos(branchAng);
    const bey       = by_ + branchLen * Math.sin(branchAng);

    g.lineStyle(0.8, 0x88b0cc, 0.62);
    drawJaggedLine(g, bx, by_, bex, bey, 2 + Math.floor(rng() * 2), 3 + rng() * 3, rng);
  }
}

// Snow-covered clear hex for winter theme: no grass, white/blue-grey snow patches + fine dots.
const SNOW_PATCH_COLORS  = [0xe8f0f8, 0xf0f5fb, 0xdde8f2, 0xf4f8fd, 0xe0ecf6];
const SNOW_SHADOW_COLORS = [0xb8cce0, 0xacc4dc, 0xc4d4e8, 0xb0c8de];

function drawSnowClearDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col, row);
  const r   = HEX_SIZE;

  // 3–5 uneven snow patches (same non-overlap logic as grassland)
  const numPatches = 3 + Math.floor(rng() * 3);
  const placed: { x: number; y: number; rad: number }[] = [];

  for (let attempt = 0; attempt < numPatches * 8 && placed.length < numPatches; attempt++) {
    const angle = rng() * Math.PI * 2;
    const dist  = rng() * r * 0.60;
    const pw    = r * (0.28 + rng() * 0.30);
    const ph    = r * (0.16 + rng() * 0.18);
    const px    = cx + Math.cos(angle) * dist;
    const py    = cy + Math.sin(angle) * dist * 0.65;
    const rad   = (pw + ph) / 4;

    if (placed.some(p => { const dx = px - p.x, dy = py - p.y; return Math.sqrt(dx * dx + dy * dy) < rad + p.rad; })) continue;
    placed.push({ x: px, y: py, rad });

    // Shadow layer first (slightly darker blue-grey)
    const shadow = SNOW_SHADOW_COLORS[Math.floor(rng() * SNOW_SHADOW_COLORS.length)];
    g.fillStyle(shadow, 0.18 + rng() * 0.12);
    g.fillEllipse(px + 1.5, py + 1.5, pw, ph);

    const color = SNOW_PATCH_COLORS[Math.floor(rng() * SNOW_PATCH_COLORS.length)];
    g.fillStyle(color, 0.32 + rng() * 0.22);
    g.fillEllipse(px, py, pw, ph);
  }

  // Fine snow speckles
  const numDots = 4 + Math.floor(rng() * 5);
  for (let i = 0; i < numDots; i++) {
    const angle = rng() * Math.PI * 2;
    const dist  = rng() * r * 0.68;
    g.fillStyle(0xf8fcff, 0.55 + rng() * 0.35);
    g.fillCircle(cx + Math.cos(angle) * dist, cy + Math.sin(angle) * dist * 0.65, 0.6 + rng() * 1.0);
  }
}

// Grassland: non-overlapping tonal patches + scattered speckles, unique per hex.
// Colours stay close to the base (#92aa48) so variation is subtle.
const GRASS_PATCH_COLORS = [0x9eb855, 0xa8bc5c, 0x849c42, 0x7e9840, 0x9ab048, 0x8aaa50];
const GRASS_SPECK_COLORS = [0x728a30, 0x5e7828, 0x7a9238, 0x526820];

function drawGrasslandDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col, row);
  const r   = HEX_SIZE;

  // 3–6 non-overlapping elliptical patches
  const numPatches = 3 + Math.floor(rng() * 4);
  const placed: { x: number; y: number; rad: number }[] = [];

  for (let attempt = 0; attempt < numPatches * 8 && placed.length < numPatches; attempt++) {
    const angle = rng() * Math.PI * 2;
    const dist  = rng() * r * 0.62;
    const pw    = r * (0.26 + rng() * 0.28);
    const ph    = r * (0.16 + rng() * 0.20);
    const px    = cx + Math.cos(angle) * dist;
    const py    = cy + Math.sin(angle) * dist * 0.65;
    const rad   = (pw + ph) / 4; // approx circle radius for overlap test

    const overlaps = placed.some(p => {
      const dx = px - p.x, dy = py - p.y;
      return Math.sqrt(dx * dx + dy * dy) < rad + p.rad;
    });
    if (overlaps) continue;

    placed.push({ x: px, y: py, rad });
    const color = GRASS_PATCH_COLORS[Math.floor(rng() * GRASS_PATCH_COLORS.length)];
    g.fillStyle(color, 0.30 + rng() * 0.22);
    g.fillEllipse(px, py, pw, ph);
  }

  // 3–6 tiny dark speckles
  const numSpeckles = 3 + Math.floor(rng() * 4);
  for (let i = 0; i < numSpeckles; i++) {
    const angle = rng() * Math.PI * 2;
    const dist  = rng() * r * 0.70;
    const color = GRASS_SPECK_COLORS[Math.floor(rng() * GRASS_SPECK_COLORS.length)];
    g.fillStyle(color, 0.45 + rng() * 0.30);
    g.fillCircle(cx + Math.cos(angle) * dist, cy + Math.sin(angle) * dist * 0.65, 0.8 + rng() * 1.2);
  }
}

// Aerial-view forest: overlapping circular canopies, dark-outer / bright-centre layering.
function drawCanopy(g: Phaser.GameObjects.Graphics, x: number, y: number, r: number, hue: number): void {
  // hue shifts slightly per tree so the mass reads as varied, not uniform
  g.fillStyle(0x1e4d0a + hue, 1);
  g.fillCircle(x, y, r);                               // dark outer ring
  g.fillStyle(0x347a14 + hue, 1);
  g.fillCircle(x, y, r * 0.76);                        // mid green body
  g.fillStyle(0x52a825 + hue, 1);
  g.fillCircle(x, y, r * 0.46);                        // bright dome centre
  g.fillStyle(0x70cc38, 0.55);
  g.fillCircle(x - r * 0.20, y - r * 0.20, r * 0.20); // specular shimmer
}

// Winter canopy: dark spruce needles + snow cap on top half.
function drawWinterCanopy(g: Phaser.GameObjects.Graphics, x: number, y: number, r: number, hue: number): void {
  g.fillStyle(0x122808 + hue, 1);
  g.fillCircle(x, y, r);               // deep dark-green ring
  g.fillStyle(0x1e4210 + hue, 1);
  g.fillCircle(x, y, r * 0.76);        // needle body
  g.fillStyle(0x2a5818 + hue, 1);
  g.fillCircle(x, y, r * 0.46);        // darker centre (less light under snow)
  // Snow cap — ellipse covering top ~55 % of canopy
  g.fillStyle(0xeef6fc, 0.82);
  g.fillEllipse(x, y - r * 0.22, r * 1.40, r * 0.80);
  // Highlight on snow cap
  g.fillStyle(0xfbfeff, 0.45);
  g.fillEllipse(x - r * 0.12, y - r * 0.35, r * 0.60, r * 0.28);
}

const FOREST_FLOOR_COLORS = [0x3a5618, 0x304c12, 0x445e1c, 0x2c4810];

function drawForestDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col, row);
  const r   = HEX_SIZE;

  // Floor texture: subtle dark-green patches in the gaps between canopies
  for (let i = 0; i < 4; i++) {
    const a = rng() * Math.PI * 2;
    const d = rng() * r * 0.65;
    g.fillStyle(FOREST_FLOOR_COLORS[Math.floor(rng() * FOREST_FLOOR_COLORS.length)], 0.28 + rng() * 0.20);
    g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.70,
      r * (0.30 + rng() * 0.30), r * (0.20 + rng() * 0.20));
  }

  // 8–15 randomly placed canopies sized to fill most of the hex
  const numTrees = 8 + Math.floor(rng() * 8);
  const placed: { x: number; y: number; rad: number }[] = [];

  for (let attempt = 0; attempt < numTrees * 14 && placed.length < numTrees; attempt++) {
    const angle = rng() * Math.PI * 2;
    const dist  = rng() * r * 0.54;
    const treeR = 8 + rng() * 8;          // radius 8–16
    const tx    = cx + Math.cos(angle) * dist;
    const ty    = cy + Math.sin(angle) * dist;

    // Allow natural overlap but reject near-concentric placement
    const tooClose = placed.some(p => {
      const dx = tx - p.x, dy = ty - p.y;
      return Math.sqrt(dx * dx + dy * dy) < (treeR + p.rad) * 0.45;
    });
    if (tooClose) continue;

    placed.push({ x: tx, y: ty, rad: treeR });
    const hue = Math.floor(rng() * 8) * 0x010101;
    drawCanopy(g, tx, ty, treeR, hue);
  }

  // Rust/autumn flecks — only in gaps, not under any canopy
  g.fillStyle(0xb86020, 0.65);
  const numFlecks = 2 + Math.floor(rng() * 3);
  let flecksDrawn = 0;
  for (let attempt = 0; attempt < 60 && flecksDrawn < numFlecks; attempt++) {
    const a  = rng() * Math.PI * 2;
    const d  = rng() * r * 0.72;
    const fx = cx + Math.cos(a) * d;
    const fy = cy + Math.sin(a) * d * 0.70;
    const underCanopy = placed.some(p => {
      const dx = fx - p.x, dy = fy - p.y;
      return Math.sqrt(dx * dx + dy * dy) < p.rad * 0.80;
    });
    if (underCanopy) continue;
    g.fillCircle(fx, fy, 1.8 + rng() * 1.2);
    flecksDrawn++;
  }
}

// Winter forest: snow-covered floor, dark spruce canopies with snow caps.
function drawWinterForestDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col, row);
  const r   = HEX_SIZE;

  // Snow floor — lighter blue-grey patches in the gaps between canopies
  const floorColors = [0xd0dce8, 0xc8d8e8, 0xdce8f4, 0xccd4e4];
  for (let i = 0; i < 4; i++) {
    const a = rng() * Math.PI * 2;
    const d = rng() * r * 0.65;
    g.fillStyle(floorColors[Math.floor(rng() * floorColors.length)], 0.32 + rng() * 0.22);
    g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.70,
      r * (0.30 + rng() * 0.30), r * (0.20 + rng() * 0.20));
  }

  // 8–15 snow-laden spruce canopies
  const numTrees = 8 + Math.floor(rng() * 8);
  const placed: { x: number; y: number; rad: number }[] = [];

  for (let attempt = 0; attempt < numTrees * 14 && placed.length < numTrees; attempt++) {
    const angle = rng() * Math.PI * 2;
    const dist  = rng() * r * 0.54;
    const treeR = 8 + rng() * 8;
    const tx    = cx + Math.cos(angle) * dist;
    const ty    = cy + Math.sin(angle) * dist;
    const tooClose = placed.some(p => {
      const dx = tx - p.x, dy = ty - p.y;
      return Math.sqrt(dx * dx + dy * dy) < (treeR + p.rad) * 0.45;
    });
    if (tooClose) continue;
    placed.push({ x: tx, y: ty, rad: treeR });
    const hue = Math.floor(rng() * 6) * 0x010101;
    drawWinterCanopy(g, tx, ty, treeR, hue);
  }

  // Tiny snow dots in the gaps between canopies
  const numDots = 3 + Math.floor(rng() * 4);
  let dotsDrawn = 0;
  for (let attempt = 0; attempt < 60 && dotsDrawn < numDots; attempt++) {
    const a  = rng() * Math.PI * 2;
    const d  = rng() * r * 0.72;
    const fx = cx + Math.cos(a) * d;
    const fy = cy + Math.sin(a) * d * 0.70;
    const underCanopy = placed.some(p => {
      const dx = fx - p.x, dy = fy - p.y;
      return Math.sqrt(dx * dx + dy * dy) < p.rad * 0.80;
    });
    if (underCanopy) continue;
    g.fillStyle(0xf0f8ff, 0.70 + rng() * 0.25);
    g.fillCircle(fx, fy, 1.2 + rng() * 1.4);
    dotsDrawn++;
  }
}

// Top-down building: two roof slopes meeting at a central ridge line.
// angle rotates the whole building; far side (colorFar) is the "back" slope.
function drawBuildingTopDown(
  g: Phaser.GameObjects.Graphics,
  cx: number, cy: number, w: number, h: number,
  angle: number, colorFar: number, colorNear: number,
): void {
  const c = Math.cos(angle), s = Math.sin(angle);
  const v = (dx: number, dy: number) => ({ x: cx + dx * c - dy * s, y: cy + dx * s + dy * c });

  const tl = v(-w / 2, -h / 2), tr = v(w / 2, -h / 2);
  const br = v( w / 2,  h / 2), bl = v(-w / 2,  h / 2);
  const ml = v(-w / 2, 0),      mr = v( w / 2, 0);

  g.fillStyle(colorFar, 1);
  g.fillPoints([tl, tr, mr, ml], true);   // far/dark slope
  g.fillStyle(colorNear, 1);
  g.fillPoints([ml, mr, br, bl], true);   // near/light slope
  g.lineStyle(1.2, 0x3a2010, 0.85);
  g.strokePoints([tl, tr, br, bl], true); // building outline
  g.lineStyle(1.8, 0x3a2010, 0.90);
  g.lineBetween(ml.x, ml.y, mr.x, mr.y); // roof ridge
}

const STONE_COLORS = [0xb5ada0, 0xc8c0b0, 0xa8a094, 0xd0c8bb, 0xbfb7a8];
const ROOF_PAIRS: [number, number][] = [
  [0x8b4a35, 0xc4846a],  // terracotta
  [0x7a3828, 0xb86850],  // dark red
  [0x6e5840, 0xa88868],  // warm brown
  [0x80504a, 0xbc8075],  // dusty rose
];
const ROOF_ANGLES = [0, Math.PI / 6, Math.PI / 4, Math.PI / 3, Math.PI / 2, -Math.PI / 6, -Math.PI / 4];

function drawTownDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col, row);

  // Cobblestone ground: grid of jittered stone ellipses, mortar shows as base colour
  for (let gy = -27; gy <= 27; gy += 9) {
    for (let gx = -27; gx <= 27; gx += 9) {
      const jx = gx + (rng() - 0.5) * 5;
      const jy = gy + (rng() - 0.5) * 5;
      if (jx * jx + jy * jy > 900) continue; // outside inscribed circle (r=30)
      const sw = 6.0 + rng() * 3.0;
      const sh = 4.5 + rng() * 2.0;
      g.fillStyle(STONE_COLORS[Math.floor(rng() * STONE_COLORS.length)], 0.75 + rng() * 0.25);
      g.fillEllipse(cx + jx, cy + jy, sw, sh);
    }
  }

  // Always 3 buildings; 60 % overlap threshold lets them pack into the hex
  const numBuildings = 3;
  const placed: { x: number; y: number; rad: number }[] = [];

  for (let attempt = 0; attempt < 54 && placed.length < numBuildings; attempt++) {
    const bw    = 15 + rng() * 11;  // 15–26 px
    const bh    = 11 + rng() *  7;  // 11–18 px
    const angle = ROOF_ANGLES[Math.floor(rng() * ROOF_ANGLES.length)];
    const dx    = (rng() - 0.5) * 24;
    const dy    = (rng() - 0.5) * 24;
    const bx    = cx + dx, by = cy + dy;
    const brad  = Math.sqrt((bw / 2) ** 2 + (bh / 2) ** 2) + 2;

    if (Math.sqrt(dx * dx + dy * dy) + brad > 35) continue; // too close to edge
    const tooClose = placed.some(p => {
      const ddx = bx - p.x, ddy = by - p.y;
      return Math.sqrt(ddx * ddx + ddy * ddy) < (brad + p.rad) * 0.60;
    });
    if (tooClose) continue;

    placed.push({ x: bx, y: by, rad: brad });
    const [colorFar, colorNear] = ROOF_PAIRS[Math.floor(rng() * ROOF_PAIRS.length)];
    drawBuildingTopDown(g, bx, by, bw, bh, angle, colorFar, colorNear);
  }
}

// ── River: neighbour-aware curved water band ─────────────────────────────────

// Flat-edge midpoints relative to hex centre (dirs 0–5: NE E SE SW W NW).
// Each point sits on the inward face of the hex at the apothem distance.
const _R3     = Math.sqrt(3);
const APOTHEM = HEX_SIZE * _R3 / 2;  // centre → flat-edge distance ≈ 34.64
const EDGE_MID = [
  { x:  APOTHEM / 2, y: -APOTHEM * _R3 / 2 }, // 0 NE
  { x:  APOTHEM,     y:  0                  }, // 1 E
  { x:  APOTHEM / 2, y:  APOTHEM * _R3 / 2  }, // 2 SE
  { x: -APOTHEM / 2, y:  APOTHEM * _R3 / 2  }, // 3 SW
  { x: -APOTHEM,     y:  0                  }, // 4 W
  { x: -APOTHEM / 2, y: -APOTHEM * _R3 / 2  }, // 5 NW
];

// Returns edge directions (0–5) that share a border with a river or bridge hex.
// Bridge neighbours are only included when they sit on the actual river (≥2 real river neighbours),
// so land approach-bridge hexes don't distort the river path of adjacent river tiles.
function riverEdgeDirs(col: number, row: number, terrain: TerrainHex[]): number[] {
  const nbrs: [number, number][] = row % 2 === 0
    ? [[col, row-1], [col+1, row], [col, row+1], [col-1, row+1], [col-1, row], [col-1, row-1]]
    : [[col+1, row-1], [col+1, row], [col+1, row+1], [col, row+1], [col-1, row], [col, row-1]];
  return nbrs.reduce<number[]>((acc, [nc, nr], d) => {
    const t = terrain.find(h => h.col === nc && h.row === nr);
    if (!t) return acc;
    if (t.type === 'river') { acc.push(d); return acc; }
    if (t.type === 'bridge') {
      // Include this bridge only if it truly crosses the river (has ≥2 actual river neighbours)
      const bnbrs: [number, number][] = nr % 2 === 0
        ? [[nc, nr-1], [nc+1, nr], [nc, nr+1], [nc-1, nr+1], [nc-1, nr], [nc-1, nr-1]]
        : [[nc+1, nr-1], [nc+1, nr], [nc+1, nr+1], [nc, nr+1], [nc-1, nr], [nc, nr-1]];
      const riverCount = bnbrs.filter(([bc, br]) =>
        terrain.find(h => h.col === bc && h.row === br)?.type === 'river'
      ).length;
      if (riverCount >= 2) acc.push(d);
    }
    return acc;
  }, []);
}

// Like riverEdgeDirs but only considers actual 'river' type (ignores bridge-bridge chains).
// Used so bridge hexes on land don't inherit a spurious water channel.
function riverOnlyEdgeDirs(col: number, row: number, terrain: TerrainHex[]): number[] {
  const nbrs: [number, number][] = row % 2 === 0
    ? [[col, row-1], [col+1, row], [col, row+1], [col-1, row+1], [col-1, row], [col-1, row-1]]
    : [[col+1, row-1], [col+1, row], [col+1, row+1], [col, row+1], [col-1, row], [col, row-1]];
  return nbrs.reduce<number[]>((acc, [nc, nr], d) => {
    if (terrain.find(h => h.col === nc && h.row === nr)?.type === 'river') acc.push(d);
    return acc;
  }, []);
}

// Returns the bridge overlay angle (radians) by detecting which axis adjacent bridge hexes lie on.
// Dir-pair 0/3 = NE-SW (-π/3), 1/4 = E-W (0), 2/5 = SE-NW (+π/3).
function bridgeAngle(col: number, row: number, terrain: TerrainHex[]): number {
  const nbrs: [number, number][] = row % 2 === 0
    ? [[col, row-1], [col+1, row], [col, row+1], [col-1, row+1], [col-1, row], [col-1, row-1]]
    : [[col+1, row-1], [col+1, row], [col+1, row+1], [col, row+1], [col-1, row], [col, row-1]];
  for (let d = 0; d < 6; d++) {
    const [nc, nr] = nbrs[d];
    if (terrain.find(h => h.col === nc && h.row === nr)?.type === 'bridge') {
      if (d === 0 || d === 3) return -Math.PI / 3; // NE-SW
      if (d === 2 || d === 5) return  Math.PI / 3; // SE-NW
      return 0;                                      // E-W
    }
  }
  return 0;
}

// For a board-edge river tile with only one river neighbour, pick the best
// off-board (void) exit edge — prefer non-opposite directions so the tile curves.
function voidExitDir(col: number, row: number, entryDir: number): number {
  const nbrs: [number, number][] = row % 2 === 0
    ? [[col, row-1], [col+1, row], [col, row+1], [col-1, row+1], [col-1, row], [col-1, row-1]]
    : [[col+1, row-1], [col+1, row], [col+1, row+1], [col, row+1], [col-1, row], [col, row-1]];
  const voidDirs: number[] = [];
  for (let d = 0; d < 6; d++) {
    if (d === entryDir) continue;
    const [nc, nr] = nbrs[d];
    if (!isValidHex(nc, nr)) voidDirs.push(d);
  }
  if (voidDirs.length === 0) return (entryDir + 3) % 6;
  const oppDir   = (entryDir + 3) % 6;
  const preferred = voidDirs.filter(d => d !== oppDir);
  const candidates = preferred.length > 0 ? preferred : voidDirs;
  return candidates[(col * 7 + row * 13) % candidates.length];
}

// Cubic Bézier evaluated at `steps` points.
function cubicBezierPts(
  x0: number, y0: number, x1: number, y1: number,
  x2: number, y2: number, x3: number, y3: number, steps = 32,
): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps, mt = 1 - t;
    pts.push({
      x: mt**3*x0 + 3*mt**2*t*x1 + 3*mt*t**2*x2 + t**3*x3,
      y: mt**3*y0 + 3*mt**2*t*y1 + 3*mt*t**2*y2 + t**3*y3,
    });
  }
  return pts;
}

const RIVER_GRASS_COLORS = [0x8aa040, 0x94aa45, 0x7e9838, 0x88a040, 0x96a848];
const RIVER_SPECK_COLORS = [0x526820, 0x486018, 0x607030];

function drawRiverDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number,
  col: number, row: number, terrain: TerrainHex[],
  overrideDirs?: [number, number],
): void {
  const rng = makeRng(col + 500, row + 300);

  // Waterlogged grassland background (slightly muted vs plain grass)
  for (let i = 0; i < 7; i++) {
    const a = rng() * Math.PI * 2, d = rng() * HEX_SIZE * 0.65;
    const pw = HEX_SIZE * (0.24 + rng() * 0.28), ph = HEX_SIZE * (0.15 + rng() * 0.18);
    g.fillStyle(RIVER_GRASS_COLORS[Math.floor(rng() * RIVER_GRASS_COLORS.length)], 0.28 + rng() * 0.20);
    g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.65, pw, ph);
  }
  for (let i = 0; i < 5; i++) {
    const a = rng() * Math.PI * 2, d = rng() * HEX_SIZE * 0.70;
    g.fillStyle(RIVER_SPECK_COLORS[Math.floor(rng() * RIVER_SPECK_COLORS.length)], 0.40 + rng() * 0.30);
    g.fillCircle(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.65, 0.8 + rng() * 1.1);
  }

  // Determine which edges connect to river/bridge neighbours.
  // overrideDirs bypasses auto-detection entirely (used by bridge hexes to avoid bridge→bridge chain).
  let dirs: number[];
  if (overrideDirs) {
    dirs = [...overrideDirs];
  } else {
    dirs = riverEdgeDirs(col, row, terrain);
    if (dirs.length === 1) dirs = [dirs[0], voidExitDir(col, row, dirs[0])];
    if (dirs.length < 2) dirs = [1, 4];
  }

  const ea = EDGE_MID[dirs[0]], eb = EDGE_MID[dirs[1]];

  const tensionA = APOTHEM * (0.40 + rng() * 0.45);
  const tensionB = APOTHEM * (0.40 + rng() * 0.45);

  const P0x = cx + ea.x,  P0y = cy + ea.y;
  const P1x = P0x - ea.x / APOTHEM * tensionA,  P1y = P0y - ea.y / APOTHEM * tensionA;
  const P3x = cx + eb.x,  P3y = cy + eb.y;
  const P2x = P3x - eb.x / APOTHEM * tensionB,  P2y = P3y - eb.y / APOTHEM * tensionB;

  let pts: { x: number; y: number }[];
  if ((dirs[0] + 3) % 6 === dirs[1]) {
    // Opposite edges produce a straight line with the standard formula.
    // Fix: route through an interior waypoint offset perpendicularly from centre.
    // The 90° boundary tangents (P1, P2 along inward normals) are preserved, so
    // joins to adjacent river tiles remain seamless.
    const flowX = eb.x - ea.x, flowY = eb.y - ea.y;
    const fLen  = Math.sqrt(flowX * flowX + flowY * flowY);
    const nfX = flowX / fLen, nfY = flowY / fLen;
    const pX  = -nfY,         pY  = nfX;            // perpendicular unit vector

    const side   = rng() < 0.5 ? 1 : -1;
    const midOff = APOTHEM * (0.18 + rng() * 0.28); // 6–16 px off centre
    const Mx = cx + pX * side * midOff;
    const My = cy + pY * side * midOff;

    const tMid = APOTHEM * 0.42;
    const seg1 = cubicBezierPts(P0x, P0y, P1x, P1y,
                                Mx - nfX * tMid, My - nfY * tMid, Mx, My);
    const seg2 = cubicBezierPts(Mx, My,
                                Mx + nfX * tMid, My + nfY * tMid, P2x, P2y, P3x, P3y);
    pts = [...seg1, ...seg2.slice(1)];
  } else {
    pts = cubicBezierPts(P0x, P0y, P1x, P1y, P2x, P2y, P3x, P3y);
  }

  // Layered river: dark muddy bank → deep water → mid blue → highlight → glint
  g.lineStyle(18, 0x2a4a18, 1.0); g.strokePoints(pts);
  g.lineStyle(13, 0x1a3a7a, 1.0); g.strokePoints(pts);
  g.lineStyle( 8, 0x3870b8, 0.9); g.strokePoints(pts);
  g.lineStyle( 4, 0x78b0d8, 0.75); g.strokePoints(pts);
  g.lineStyle(1.5, 0xd0eaf8, 0.55); g.strokePoints(pts);
}

// ── Hedgerow detail ───────────────────────────────────────────────────────────
// Bocage: 5×5 grid of nodes inside the hex; ~half the horizontal/vertical
// adjacent pairs are connected with a straight segment of oriented ovals.
// Right-angle structure, short segments, all within hex bounds, maze-like gaps.

function drawHedgerowDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col + 200, row + 400);
  const r   = HEX_SIZE;

  drawGrasslandDetail(g, cx, cy, col, row);

  // Blobs are wider than before: ry ≈ 58–72 % of rx
  const rx = 4.5 + rng() * 1.5;
  const ry = rx  * (0.58 + rng() * 0.14);

  function ovalBlob(bx: number, by: number, angle: number): void {
    const c = Math.cos(angle), s = Math.sin(angle);
    const pts = (orx: number, ory: number) => Array.from({ length: 12 }, (_, k) => {
      const t = (k / 12) * Math.PI * 2;
      const lx = orx * Math.cos(t), ly = ory * Math.sin(t);
      return { x: bx + lx * c - ly * s, y: by + lx * s + ly * c };
    });
    g.fillStyle(0x152e05, 1.00); g.fillPoints(pts(rx,        ry        ), true);
    g.fillStyle(0x2e6010, 1.00); g.fillPoints(pts(rx * 0.72, ry * 0.72 ), true);
    g.fillStyle(0x68a828, 0.50); g.fillPoints(pts(rx * 0.40, ry * 0.40 ), true);
  }

  // Straight segment between two points — hard-clipped to hex interior
  function drawSegment(x0: number, y0: number, x1: number, y1: number): void {
    const dx = x1 - x0, dy = y1 - y0;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) return;
    const angle = Math.atan2(dy, dx);
    const step  = rx * 1.40;
    for (let d = 0; d <= len; d += step) {
      const bx = x0 + dx * (d / len), by = y0 + dy * (d / len);
      if ((bx - cx) ** 2 + (by - cy) ** 2 <= (r * 0.88) ** 2) ovalBlob(bx, by, angle);
    }
  }

  // Build 5×5 grid; keep only nodes that fall inside the hex
  const spacing = r * 0.40;  // ~16 px between nodes
  type Node = { x: number; y: number; gx: number; gy: number };
  const nodes: Node[] = [];
  for (let gy = -2; gy <= 2; gy++) {
    for (let gx = -2; gx <= 2; gx++) {
      // Small random jitter so it doesn't look too mechanical
      const px = cx + gx * spacing + (rng() - 0.5) * spacing * 0.22;
      const py = cy + gy * spacing + (rng() - 0.5) * spacing * 0.22;
      if ((px - cx) ** 2 + (py - cy) ** 2 <= (r * 0.86) ** 2) nodes.push({ x: px, y: py, gx, gy });
    }
  }

  // Connect adjacent pairs (horizontal OR vertical) with ~50 % probability
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i], b = nodes[j];
      const adjH = b.gx === a.gx + 1 && b.gy === a.gy;
      const adjV = b.gy === a.gy + 1 && b.gx === a.gx;
      if ((adjH || adjV) && rng() > 0.50) drawSegment(a.x, a.y, b.x, b.y);
    }
  }
}

// ── Hill detail ───────────────────────────────────────────────────────────────
// Grassland base with a large prominent summit circle, then dense short radial
// strokes that begin on average halfway between centre and hex edge.

function drawHillDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col + 100, row + 200);
  const r   = HEX_SIZE;

  drawGrasslandDetail(g, cx, cy, col, row);

  // Summit — much larger: fills roughly the inner 50 % radius of the hex
  const ox = cx + (rng() - 0.5) * 5, oy = cy + (rng() - 0.5) * 5;
  g.fillStyle(0xc4d460, 0.36);
  g.fillEllipse(ox, oy, r * 1.30, r * 1.14);   // outer soft glow
  g.fillStyle(0xd0de68, 0.28);
  g.fillEllipse(ox, oy, r * 0.95, r * 0.84);   // mid ring — visually "the summit"
  g.fillStyle(0xdce878, 0.20);
  g.fillEllipse(ox, oy, r * 0.62, r * 0.54);   // bright centre

  // Dense radial slope strokes — lines start ~halfway out, end near hex edge
  const numLines = 160 + Math.floor(rng() * 20);
  for (let i = 0; i < numLines; i++) {
    const angle  = (i / numLines) * Math.PI * 2 + (rng() - 0.5) * 0.08;
    // startR centred around r*0.50 — some variation but average halfway out
    const startR = r * (0.34 + rng() * 0.30);   // range 0.34–0.64, avg ~0.49
    const endR   = r * (0.68 + rng() * 0.26);   // range 0.68–0.94, irregular outer edge
    g.lineStyle(0.5 + rng() * 0.45, 0x2a1e08, 0.32 + rng() * 0.28);
    g.lineBetween(
      cx + Math.cos(angle) * startR, cy + Math.sin(angle) * startR,
      cx + Math.cos(angle) * endR,   cy + Math.sin(angle) * endR,
    );
  }
}

// Winter hill: snow-covered slopes, icy blue-white summit highlight.
function drawWinterHillDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col + 100, row + 200);
  const r   = HEX_SIZE;

  drawSnowClearDetail(g, cx, cy, col, row);

  // Summit — same off-centre origin, but in cold blue-white instead of lime
  const ox = cx + (rng() - 0.5) * 5, oy = cy + (rng() - 0.5) * 5;
  g.fillStyle(0xd0e4f8, 0.36);
  g.fillEllipse(ox, oy, r * 1.30, r * 1.14);   // outer cold glow
  g.fillStyle(0xe0eefb, 0.28);
  g.fillEllipse(ox, oy, r * 0.95, r * 0.84);   // mid ring
  g.fillStyle(0xf0f8ff, 0.22);
  g.fillEllipse(ox, oy, r * 0.62, r * 0.54);   // bright icy peak

  // Slope strokes in cool grey-blue (snow-covered rocky slopes)
  const numLines = 160 + Math.floor(rng() * 20);
  for (let i = 0; i < numLines; i++) {
    const angle  = (i / numLines) * Math.PI * 2 + (rng() - 0.5) * 0.08;
    const startR = r * (0.34 + rng() * 0.30);
    const endR   = r * (0.68 + rng() * 0.26);
    g.lineStyle(0.5 + rng() * 0.45, 0x7a8898, 0.28 + rng() * 0.24);
    g.lineBetween(
      cx + Math.cos(angle) * startR, cy + Math.sin(angle) * startR,
      cx + Math.cos(angle) * endR,   cy + Math.sin(angle) * endR,
    );
  }
}

// Red X drawn over impassable-hill hexes to signal "no entry".
function drawImpassableMarker(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const r = HEX_SIZE * 0.42;
  g.lineStyle(3.5, 0xcc1800, 0.80);
  g.lineBetween(cx - r, cy - r * 0.80, cx + r, cy + r * 0.80);
  g.lineBetween(cx + r, cy - r * 0.80, cx - r, cy + r * 0.80);
}

// ── Ravine detail ────────────────────────────────────────────────────────────
// Grassland/snow base cut by a jagged eroded gully band running across the hex.
function drawRavineDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number, winter: boolean,
): void {
  const rng = makeRng(col + 300, row + 600);
  const r   = HEX_SIZE;

  if (winter) drawSnowClearDetail(g, cx, cy, col, row);
  else        drawGrasslandDetail(g, cx, cy, col, row);

  const angle = rng() * Math.PI; // gully runs across the hex at a random axis
  const dx = Math.cos(angle), dy = Math.sin(angle);
  const halfLen = r * 1.05;
  const x1 = cx - dx * halfLen, y1 = cy - dy * halfLen;
  const x2 = cx + dx * halfLen, y2 = cy + dy * halfLen;
  const nx = -dy, ny = dx; // perpendicular
  const width = r * 0.42;

  const edgeA = Array.from({ length: 9 }, (_, i) => {
    const t = i / 8;
    const px = x1 + (x2 - x1) * t, py = y1 + (y2 - y1) * t;
    const w  = width / 2 + (rng() - 0.5) * 6;
    return { x: px + nx * w, y: py + ny * w };
  });
  const edgeB = Array.from({ length: 9 }, (_, i) => {
    const t = i / 8;
    const px = x1 + (x2 - x1) * t, py = y1 + (y2 - y1) * t;
    const w  = width / 2 + (rng() - 0.5) * 6;
    return { x: px - nx * w, y: py - ny * w };
  }).reverse();

  const earthDark  = winter ? 0x5a5850 : 0x4a3418;
  const earthMid   = winter ? 0x746e64 : 0x6a4c24;
  const rimHighlight = winter ? 0xd8e4ee : 0xb89858;

  g.fillStyle(earthDark, 0.92);
  g.fillPoints([...edgeA, ...edgeB], true);

  // Inner shadow band (deepest part of the gully)
  const innerA = edgeA.map(p => ({ x: cx + (p.x - cx) * 0.55, y: cy + (p.y - cy) * 0.55 }));
  const innerB = edgeB.map(p => ({ x: cx + (p.x - cx) * 0.55, y: cy + (p.y - cy) * 0.55 }));
  g.fillStyle(0x241608, 0.55);
  g.fillPoints([...innerA, ...innerB], true);

  // Rim highlight along both edges
  g.lineStyle(1.4, rimHighlight, 0.55);
  g.strokePoints(edgeA, false);
  g.strokePoints(edgeB, false);

  // Scattered rocks/scree in the gully floor
  for (let i = 0; i < 6; i++) {
    const t = rng();
    const px = x1 + (x2 - x1) * t + (rng() - 0.5) * 6;
    const py = y1 + (y2 - y1) * t + (rng() - 0.5) * 6;
    if ((px - cx) ** 2 + (py - cy) ** 2 > (r * 0.9) ** 2) continue;
    g.fillStyle(earthMid, 0.7);
    g.fillCircle(px, py, 1.4 + rng() * 1.6);
  }
}

// ── Trench detail ────────────────────────────────────────────────────────────
// Grassland/snow base with a zigzag dug trench line, earth-brown channel + sandbagged rim.
function drawTrenchDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number, winter: boolean,
): void {
  const rng = makeRng(col + 500, row + 900);
  const r   = HEX_SIZE;

  if (winter) drawSnowClearDetail(g, cx, cy, col, row);
  else        drawGrasslandDetail(g, cx, cy, col, row);

  const baseAngle = rng() * Math.PI;
  const dx = Math.cos(baseAngle), dy = Math.sin(baseAngle);
  const nx = -dy, ny = dx;

  // Zigzag spine: alternating steps forward and to the side (fire-bay pattern)
  const numSegs = 5;
  const segLen  = (r * 2.0) / numSegs;
  const spine: { x: number; y: number }[] = [];
  let px = cx - dx * r, py = cy - dy * r;
  spine.push({ x: px, y: py });
  for (let i = 0; i < numSegs; i++) {
    px += dx * segLen;
    py += dy * segLen;
    const jog = (i % 2 === 0 ? 1 : -1) * r * 0.30;
    spine.push({ x: px + nx * jog, y: py + ny * jog });
  }

  const trenchW = r * 0.30;
  const outerL = spine.map(p => ({ x: p.x + nx * trenchW / 2, y: p.y + ny * trenchW / 2 }));
  const outerR = spine.map(p => ({ x: p.x - nx * trenchW / 2, y: p.y - ny * trenchW / 2 })).reverse();

  g.fillStyle(0x3a2a14, 0.92);
  g.fillPoints([...outerL, ...outerR], true);
  g.fillStyle(0x1c1408, 0.6);
  const innerL = outerL.map(p => ({ x: cx + (p.x - cx) * 0.6, y: cy + (p.y - cy) * 0.6 }));
  const innerR = outerR.map(p => ({ x: cx + (p.x - cx) * 0.6, y: cy + (p.y - cy) * 0.6 }));
  g.fillPoints([...innerL, ...innerR], true);

  // Sandbagged parapet dots along the rim
  const parapetColor = winter ? 0xc8b898 : 0xa89060;
  for (const edge of [outerL, outerR]) {
    for (let i = 0; i < edge.length - 1; i++) {
      const mx = (edge[i].x + edge[i + 1].x) / 2, my = (edge[i].y + edge[i + 1].y) / 2;
      g.fillStyle(parapetColor, 0.75);
      g.fillEllipse(mx, my, 4.5, 3);
    }
  }

  if (winter) {
    g.fillStyle(0xeef6fc, 0.5);
    for (const p of spine) g.fillEllipse(p.x, p.y - trenchW * 0.3, trenchW * 0.9, 2.2);
  }
}

// ── City Ruins detail ─────────────────────────────────────────────────────────
// Rubble-strewn cobblestone ground with broken/collapsed building shells.
const RUBBLE_SHADOW_COLORS = [0x3c342a, 0x342c22, 0x453a2e];
const RUBBLE_STONE_COLORS  = [0x9a8f7c, 0xaca090, 0x8c8070, 0xb8ab98];
const RUINS_WALL_COLORS: [number, number][] = [
  [0x5a2e1c, 0x9a5638],  // brick red, shadow/lit
  [0x4a3a2c, 0x8a7258],  // dusty tan, shadow/lit
  [0x463228, 0x7c5c44],  // rubble brown, shadow/lit
];

function drawCityRuinsDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number, winter: boolean,
): void {
  const rng = makeRng(col + 700, row + 1100);

  // Rubble-covered ground: dark shadow specks first, lighter stone chips on top
  for (let gy = -27; gy <= 27; gy += 8) {
    for (let gx = -27; gx <= 27; gx += 8) {
      const jx = gx + (rng() - 0.5) * 5, jy = gy + (rng() - 0.5) * 5;
      if (jx * jx + jy * jy > 900) continue;
      const sw = 6.5 + rng() * 3.5, sh = 5.0 + rng() * 2.5;
      g.fillStyle(RUBBLE_SHADOW_COLORS[Math.floor(rng() * RUBBLE_SHADOW_COLORS.length)], 0.55);
      g.fillEllipse(cx + jx + 1, cy + jy + 1, sw, sh);
      g.fillStyle(RUBBLE_STONE_COLORS[Math.floor(rng() * RUBBLE_STONE_COLORS.length)], 0.85);
      g.fillEllipse(cx + jx, cy + jy, sw * 0.85, sh * 0.85);
    }
  }

  // Two broken building shells: jagged low wall stub with punched-out windows
  const placedBuildings: { x: number; y: number }[] = [];
  for (let b = 0; b < 2; b++) {
    const bw = 18 + rng() * 8, bh = 13 + rng() * 6;
    const dx = (rng() - 0.5) * 24, dy = (rng() - 0.5) * 24;
    const bx = cx + dx, by = cy + dy;
    placedBuildings.push({ x: bx, y: by });
    const wallH = 5 + rng() * 4;
    const jag = Array.from({ length: 7 }, (_, i) => {
      const t = i / 6;
      const wx = bx - bw / 2 + bw * t;
      const wy = by - bh / 2 - (rng() * wallH);
      return { x: wx, y: wy };
    });
    const outline = [
      { x: bx - bw / 2, y: by + bh / 2 },
      ...jag,
      { x: bx + bw / 2, y: by + bh / 2 },
    ];

    // Ground shadow the wall sits on
    g.fillStyle(0x241c14, 0.4);
    g.fillEllipse(bx, by + bh / 2 + 1, bw * 1.05, 6);

    const [wallShadow, wallLit] = RUINS_WALL_COLORS[Math.floor(rng() * RUINS_WALL_COLORS.length)];
    g.fillStyle(wallShadow, 1);
    g.fillPoints(outline, true);
    // Lit face — offset inward strip to suggest a sunlit wall plane
    const lit = outline.map(p => ({ x: p.x, y: p.y + (p.y > by ? 0 : 2) }));
    g.fillStyle(wallLit, 0.55);
    g.fillPoints(lit, true);
    g.lineStyle(1.3, 0x1c140c, 0.85);
    g.strokePoints(outline, true);

    // Punched-out window/breach gaps
    const numGaps = 1 + Math.floor(rng() * 2);
    for (let i = 0; i < numGaps; i++) {
      const gx2 = bx - bw / 2 + 4 + rng() * (bw - 8);
      const gy2 = by + bh / 2 - 3 - rng() * (bh * 0.4);
      g.fillStyle(0x1c140c, 0.75);
      g.fillRect(gx2 - 2, gy2 - 3, 4, 5);
    }
  }

  // Rubble piles scattered between the walls (with tiny shadow for depth)
  for (let i = 0; i < 7; i++) {
    const a = rng() * Math.PI * 2, d = rng() * HEX_SIZE * 0.62;
    const px = cx + Math.cos(a) * d, py = cy + Math.sin(a) * d * 0.7;
    const tooCloseToBuilding = placedBuildings.some(p => Math.hypot(px - p.x, py - p.y) < 10);
    if (tooCloseToBuilding) continue;
    g.fillStyle(0x2c241c, 0.5);
    g.fillCircle(px + 0.8, py + 0.8, 2.0 + rng() * 1.8);
    g.fillStyle(0x8a7c68, 0.85);
    g.fillCircle(px, py, 1.6 + rng() * 1.6);
  }

  if (winter) {
    // Snow caps on the wall tops + drifts on open ground
    for (const p of placedBuildings) {
      g.fillStyle(0xeef6fc, 0.75);
      g.fillEllipse(p.x, p.y - 8, 14, 4);
    }
    g.fillStyle(0xe4eef6, 0.4);
    for (let i = 0; i < 4; i++) {
      const a = rng() * Math.PI * 2, d = rng() * HEX_SIZE * 0.55;
      g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.7, 8 + rng() * 6, 4 + rng() * 3);
    }
  }
}

// ── Hill with Forest / Hill with Village ─────────────────────────────────────
// Visual-only composites: hill shading as base, sparse forest canopy or a
// couple of buildings drawn on top (rules-wise these play as plain Forest/Town).
function drawHillForestDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number, winter: boolean,
): void {
  const rng = makeRng(col + 900, row + 1300);
  if (winter) drawWinterHillDetail(g, cx, cy, col, row);
  else        drawHillDetail(g, cx, cy, col, row);

  const r = HEX_SIZE;
  const numTrees = 5 + Math.floor(rng() * 3);
  const placed: { x: number; y: number; rad: number }[] = [];
  for (let attempt = 0; attempt < numTrees * 14 && placed.length < numTrees; attempt++) {
    const angle = rng() * Math.PI * 2, dist = rng() * r * 0.50;
    const treeR = 7 + rng() * 7;
    const tx = cx + Math.cos(angle) * dist, ty = cy + Math.sin(angle) * dist;
    const tooClose = placed.some(p => Math.hypot(tx - p.x, ty - p.y) < (treeR + p.rad) * 0.55);
    if (tooClose) continue;
    placed.push({ x: tx, y: ty, rad: treeR });
    const hue = Math.floor(rng() * 6) * 0x010101;
    if (winter) drawWinterCanopy(g, tx, ty, treeR, hue);
    else        drawCanopy(g, tx, ty, treeR, hue);
  }
}

function drawHillVillageDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number, winter: boolean,
): void {
  const rng = makeRng(col + 1500, row + 1900);
  if (winter) drawWinterHillDetail(g, cx, cy, col, row);
  else        drawHillDetail(g, cx, cy, col, row);

  const numBuildings = 2;
  const placed: { x: number; y: number; rad: number }[] = [];
  for (let attempt = 0; attempt < 40 && placed.length < numBuildings; attempt++) {
    const bw = 15 + rng() * 10, bh = 11 + rng() * 6;
    const angle = ROOF_ANGLES[Math.floor(rng() * ROOF_ANGLES.length)];
    const dx = (rng() - 0.5) * 22, dy = (rng() - 0.5) * 22;
    const bx = cx + dx, by = cy + dy;
    const brad = Math.sqrt((bw / 2) ** 2 + (bh / 2) ** 2) + 2;
    if (Math.hypot(dx, dy) + brad > 32) continue;
    if (placed.some(p => Math.hypot(bx - p.x, by - p.y) < (brad + p.rad) * 0.6)) continue;
    placed.push({ x: bx, y: by, rad: brad });
    const [colorFar, colorNear] = ROOF_PAIRS[Math.floor(rng() * ROOF_PAIRS.length)];
    drawBuildingTopDown(g, bx, by, bw, bh, angle, colorFar, colorNear);
    if (winter) {
      g.fillStyle(0xeef6fc, 0.7);
      g.fillEllipse(bx, by - bh * 0.15, bw * 0.85, bh * 0.4);
    }
  }
}

// ── Factory Complex detail ────────────────────────────────────────────────────
// Concrete yard + one large industrial shed with a smokestack.
const FACTORY_ROOF: [number, number] = [0x2e2c28, 0x7a4030];  // dark shadow slope, rust-red lit slope

function drawFactoryDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number, winter: boolean,
): void {
  const rng = makeRng(col + 2100, row + 2500);
  const roofAngle = Math.PI / 9;

  // Concrete/gravel yard texture — alternating dark cracks and light chips for contrast
  for (let i = 0; i < 10; i++) {
    const a = rng() * Math.PI * 2, d = rng() * HEX_SIZE * 0.66;
    g.fillStyle(winter ? 0x6a747c : 0x3a3630, 0.35);
    g.fillEllipse(cx + Math.cos(a) * d + 1, cy + Math.sin(a) * d * 0.7 + 1, 6 + rng() * 5, 4 + rng() * 3);
    g.fillStyle(winter ? 0xc4ccd2 : 0xa8a294, 0.5 + rng() * 0.2);
    g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.7, 5 + rng() * 4, 3.5 + rng() * 2.5);
  }

  // Ground shadow beneath the shed
  g.fillStyle(0x1c1a16, 0.4);
  g.fillEllipse(cx - 3, cy + 4, 42, 14);

  // Main shed — one large building spanning most of the hex, rust-red corrugated roof
  drawBuildingTopDown(g, cx - 3, cy + 1, 40, 27, roofAngle, FACTORY_ROOF[0], FACTORY_ROOF[1]);
  // Corrugation ridges across the lit slope
  const c = Math.cos(roofAngle), s = Math.sin(roofAngle);
  for (let i = -3; i <= 3; i++) {
    const lx = i * 5.5;
    const x1 = cx - 3 + lx * c - (13.5) * -s, y1 = cy + 1 + lx * s + (13.5) * c;
    const x2 = cx - 3 + lx * c - (2) * -s,    y2 = cy + 1 + lx * s + (2) * c;
    g.lineStyle(0.8, 0x50281c, 0.5);
    g.lineBetween(x1, y1, x2, y2);
  }

  // Smokestack — attached at the building's near corner, tall and prominent
  const stackAngle = roofAngle;
  const stackBaseX = cx - 3 + 16 * Math.cos(stackAngle) - 10 * -Math.sin(stackAngle);
  const stackBaseY = cy + 1 + 16 * Math.sin(stackAngle) + 10 * Math.cos(stackAngle);
  const stackX = stackBaseX, stackY = stackBaseY - 22;
  g.fillStyle(0x453a30, 0.4);
  g.fillEllipse(stackBaseX, stackBaseY + 1, 10, 4); // base shadow
  g.fillStyle(0x6a5a48, 1);
  g.fillRect(stackX - 3.5, stackY, 7, 22);
  g.fillStyle(0x8a7a64, 1);
  g.fillRect(stackX - 3.5, stackY, 3, 22); // lit side stripe
  g.fillStyle(0x2c221a, 1);
  g.fillEllipse(stackX, stackY, 8, 3);

  if (!winter) {
    // Smoke puffs drifting up-left, growing and fading
    for (let i = 0; i < 4; i++) {
      g.fillStyle(0xd8d4cc, 0.42 - i * 0.09);
      g.fillCircle(stackX - i * 5, stackY - 6 - i * 8, 4.5 + i * 2);
    }
  } else {
    // Snow cap on the roof ridge + light drifts on the ground
    g.fillStyle(0xeef6fc, 0.7);
    g.fillEllipse(cx - 3, cy - 6, 34, 8);
    g.fillStyle(0xe4eef6, 0.4);
    for (let i = 0; i < 3; i++) {
      const a = rng() * Math.PI * 2, d = rng() * HEX_SIZE * 0.5;
      g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.6, 7 + rng() * 5, 3.5 + rng() * 2.5);
    }
  }
}

// ── Marsh detail ──────────────────────────────────────────────────────────────
// Muddy ground, dark standing-water pools, and scattered reed tufts.
const MUD_PATCH_COLORS  = [0x4a5630, 0x3e4a26, 0x54622e, 0x445024];
const WATER_POOL_COLOR  = 0x2c3a28;

function drawMarshDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number, winter: boolean,
): void {
  const rng = makeRng(col + 2900, row + 3300);
  const r   = HEX_SIZE;

  // Mottled mud patches
  for (let i = 0; i < 7; i++) {
    const a = rng() * Math.PI * 2, d = rng() * r * 0.62;
    g.fillStyle(MUD_PATCH_COLORS[Math.floor(rng() * MUD_PATCH_COLORS.length)], 0.35 + rng() * 0.25);
    g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.68, r * (0.26 + rng() * 0.24), r * (0.16 + rng() * 0.16));
  }

  // Standing-water pools with a pale reflective rim
  const numPools = 3 + Math.floor(rng() * 2);
  const pools: { x: number; y: number; w: number; h: number }[] = [];
  for (let attempt = 0; attempt < numPools * 8 && pools.length < numPools; attempt++) {
    const a = rng() * Math.PI * 2, d = rng() * r * 0.55;
    const px = cx + Math.cos(a) * d, py = cy + Math.sin(a) * d * 0.65;
    const pw = r * (0.22 + rng() * 0.16), ph = pw * (0.55 + rng() * 0.2);
    if (pools.some(p => Math.hypot(px - p.x, py - p.y) < (pw + p.w) * 0.5)) continue;
    pools.push({ x: px, y: py, w: pw, h: ph });
    g.fillStyle(WATER_POOL_COLOR, 0.85);
    g.fillEllipse(px, py, pw, ph);
    g.fillStyle(winter ? 0xc8d8dc : 0x7a9484, 0.35);
    g.fillEllipse(px - pw * 0.15, py - ph * 0.2, pw * 0.45, ph * 0.35);
  }

  // Reed tufts scattered between the pools
  for (let i = 0; i < 8; i++) {
    const a = rng() * Math.PI * 2, d = rng() * r * 0.7;
    const px = cx + Math.cos(a) * d, py = cy + Math.sin(a) * d * 0.68;
    if (pools.some(p => Math.hypot(px - p.x, py - p.y) < Math.max(p.w, p.h) * 0.6)) continue;
    const reedColor = winter ? 0x9a9678 : 0x6a7a3c;
    g.lineStyle(1.1, reedColor, 0.7 + rng() * 0.25);
    for (let k = 0; k < 3; k++) {
      const lean = (rng() - 0.5) * 4;
      g.lineBetween(px + k * 1.6 - 1.6, py + 2, px + k * 1.6 - 1.6 + lean, py - 6 - rng() * 4);
    }
  }

  if (winter) {
    // Thin snow crust on the mud between pools (pools stay unfrozen/dark)
    g.fillStyle(0xe4eef2, 0.3);
    for (let i = 0; i < 5; i++) {
      const a = rng() * Math.PI * 2, d = rng() * r * 0.58;
      const px = cx + Math.cos(a) * d, py = cy + Math.sin(a) * d * 0.68;
      if (pools.some(p => Math.hypot(px - p.x, py - p.y) < Math.max(p.w, p.h) * 0.6)) continue;
      g.fillEllipse(px, py, 7 + rng() * 5, 4 + rng() * 3);
    }
  }
}

// ── Sandbag overlay ───────────────────────────────────────────────────────────
// Sandbags face toward the enemy of the owner.  The arc covers three hex edges:
//   faceSouth=true  → SE edge → bottom vertex → SW edge  (owner defends from north)
//   faceSouth=false → NE edge → top vertex    → NW edge  (owner defends from south)
// faceSouth is derived from (owner === 'axis') XOR flipped so the arc always
// points toward the attacker in the viewer's visual space.

// [dx, dy, angle_rad]
const SOUTH_SANDBAG_POS: [number, number, number][] = [
  [ 25,  16,  Math.PI / 2  ],
  [ 21,  16, -Math.PI / 6  ],
  [ 11,  22, -Math.PI / 6  ],
  [  2,  27, -Math.PI / 6  ],
  [  0,  30,  0            ],
  [ -2,  27,  Math.PI / 6  ],
  [-11,  22,  Math.PI / 6  ],
  [-21,  16,  Math.PI / 6  ],
  [-25,  16,  Math.PI / 2  ],
];
// Mirror vertically: dy → -dy, angle → -angle
const NORTH_SANDBAG_POS: [number, number, number][] =
  SOUTH_SANDBAG_POS.map(([dx, dy, a]) => [dx, -dy, -a] as [number, number, number]);

function drawSandbagOverlay(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, faceSouth: boolean,
): void {
  function bag(bx: number, by: number, angle: number): void {
    const c = Math.cos(angle), s = Math.sin(angle);
    const oval = (rx: number, ry: number) =>
      Array.from({ length: 12 }, (_, i) => {
        const t = (i / 12) * Math.PI * 2;
        const lx = rx * Math.cos(t), ly = ry * Math.sin(t);
        return { x: bx + lx * c - ly * s, y: by + lx * s + ly * c };
      });
    g.fillStyle(0x7a5520, 0.85); g.fillPoints(oval(7.0, 4.2), true);
    g.fillStyle(0xc8a060, 1.00); g.fillPoints(oval(6.0, 3.6), true);
    g.fillStyle(0xf0d090, 0.50); g.fillPoints(oval(3.8, 2.0), true);
  }

  for (const [dx, dy, angle] of (faceSouth ? SOUTH_SANDBAG_POS : NORTH_SANDBAG_POS)) {
    bag(cx + dx, cy + dy, angle);
  }
}

// ── Barbed-wire overlay ───────────────────────────────────────────────────────
// Two parallel E-W strands centred on the hex, main faces N and S.
// Per-tile RNG adds a small tilt (±8°) and vertical shift (±5 px).

function drawBarbedWireOverlay(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng  = makeRng(col + 333, row + 777);
  const tilt = (rng() - 0.5) * 0.28;        // ±8° random tilt
  const yOff = (rng() - 0.5) * 10;          // ±5 px vertical shift

  const cos = Math.cos(tilt), sin = Math.sin(tilt);
  const W = (lx: number, ly: number) => ({
    x: cx + lx * cos - ly * sin,
    y: cy + yOff + lx * sin + ly * cos,
  });

  const halfSpan = HEX_SIZE * 0.60;  // ≈24 px — keeps wire away from hex edges
  const WIRE_C   = 0x4a4a4a;
  const BARB_C   = 0x333333;

  // Two parallel strands with a tiny sine wave for organic feel
  for (const lane of [-2.2, 2.2]) {
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i <= 28; i++) {
      const t  = i / 28;
      const lx = -halfSpan + t * halfSpan * 2;
      const ly = lane + Math.sin(t * Math.PI * 5) * 0.7;
      pts.push(W(lx, ly));
    }
    g.lineStyle(1.5, WIRE_C, 0.90);
    g.strokePoints(pts);
  }

  // X-shaped barbs evenly spaced along the wire
  const nBarbs = 6;
  const bHalf  = 5.0;
  g.lineStyle(1.2, BARB_C, 0.85);
  for (let i = 0; i < nBarbs; i++) {
    const lx = -halfSpan + (i + 0.5) / nBarbs * halfSpan * 2;
    const a = W(lx - bHalf * 0.65, -bHalf);
    const b = W(lx + bHalf * 0.65,  bHalf);
    const c = W(lx + bHalf * 0.65, -bHalf);
    const d = W(lx - bHalf * 0.65,  bHalf);
    g.lineBetween(a.x, a.y, b.x, b.y);
    g.lineBetween(c.x, c.y, d.x, d.y);
  }

  // Small dot at each barb crossing for the sharp-point feel
  g.fillStyle(BARB_C, 0.80);
  for (let i = 0; i < nBarbs; i++) {
    const lx = -halfSpan + (i + 0.5) / nBarbs * halfSpan * 2;
    const p  = W(lx, 0);
    g.fillCircle(p.x, p.y, 1.4);
  }
}

// ── Bridge overlay ────────────────────────────────────────────────────────────
// Metal truss bridge seen from above. angle=0 → E-W; angle=-π/3 → NE-SW (Arnheim style).
// Drawn on top of the river detail so the water is visible on both sides.

function drawBridgeOverlay(g: Phaser.GameObjects.Graphics, cx: number, cy: number, angle = 0): void {
  const BL = HEX_SIZE * 0.72;  // half-length
  const BW = HEX_SIZE * 0.30;  // half-width

  const co = Math.cos(angle), si = Math.sin(angle);
  // Rotate a local offset (dx, dy) to screen coords around (cx, cy).
  const rp = (dx: number, dy: number) => ({ x: cx + dx * co - dy * si, y: cy + dx * si + dy * co });
  const quad = (ax: number, ay: number, bx: number, by: number, cx2: number, cy2: number, dx: number, dy: number) =>
    g.fillPoints([rp(ax, ay), rp(bx, by), rp(cx2, cy2), rp(dx, dy)], true);
  const ln = (ax: number, ay: number, bx: number, by: number) => {
    const a = rp(ax, ay), b = rp(bx, by); g.lineBetween(a.x, a.y, b.x, b.y);
  };

  // Road deck
  g.fillStyle(0x787878, 1.0);
  quad(-BL, -BW,  BL, -BW,  BL, BW, -BL, BW);

  // Lighter centre strip
  g.fillStyle(0xb0b0b0, 0.35);
  quad(-BL, -BW * 0.30,  BL, -BW * 0.30,  BL, BW * 0.30, -BL, BW * 0.30);

  // Shadow on trailing edge
  g.fillStyle(0x000000, 0.20);
  quad(-BL, BW - 2,  BL, BW - 2,  BL, BW + 0.5, -BL, BW + 0.5);

  const STEEL = 0x202020, TRUSS = 0x2e2e2e;
  const nPanels = 8, panelW = (BL * 2) / nPanels;

  // Outer edge beams
  g.lineStyle(3.5, STEEL, 1.0);
  ln(-BL, -BW,  BL, -BW);
  ln(-BL,  BW,  BL,  BW);

  // Cross-beams
  g.lineStyle(1.5, STEEL, 0.88);
  for (let i = 0; i <= nPanels; i++) {
    const lx = -BL + i * panelW;
    ln(lx, -BW, lx, BW);
  }

  // Warren-truss diagonals
  g.lineStyle(1.2, TRUSS, 0.78);
  for (let i = 0; i < nPanels; i++) {
    const x0 = -BL + i * panelW, x1 = x0 + panelW;
    if (i % 2 === 0) ln(x0, -BW, x1, BW);
    else             ln(x0,  BW, x1, -BW);
  }

  // Rivet dots
  g.fillStyle(0x111111, 0.90);
  for (let i = 0; i <= nPanels; i++) {
    const lx = -BL + i * panelW;
    const a = rp(lx, -BW), b = rp(lx, BW);
    g.fillCircle(a.x, a.y, 1.8);
    g.fillCircle(b.x, b.y, 1.8);
  }

  // End-cap reinforcement plates
  g.fillStyle(STEEL, 1.0);
  quad(-BL - 3, -BW, -BL + 2, -BW, -BL + 2, BW, -BL - 3, BW);
  quad( BL - 2, -BW,  BL + 3, -BW,  BL + 3, BW,  BL - 2, BW);
}

// ── Ocean detail ──────────────────────────────────────────────────────────────
// Deep blue with gentle wave lines to evoke shallow landing waters.

function drawOceanDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col + 700, row + 800);

  // Subtle depth variation patches
  for (let i = 0; i < 4; i++) {
    const a  = rng() * Math.PI * 2;
    const d  = rng() * HEX_SIZE * 0.55;
    const pw = HEX_SIZE * (0.30 + rng() * 0.35);
    const ph = HEX_SIZE * (0.20 + rng() * 0.22);
    g.fillStyle(0x1060b8 + Math.floor(rng() * 4) * 0x010101, 0.25 + rng() * 0.20);
    g.fillEllipse(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.65, pw, ph);
  }

  // Three horizontal wave lines with a sine wiggle
  for (let lane = 0; lane < 3; lane++) {
    const ly = cy - 14 + lane * 14 + (rng() - 0.5) * 4;
    const pts: { x: number; y: number }[] = [];
    for (let j = 0; j <= 24; j++) {
      const t = j / 24;
      pts.push({ x: cx - 28 + t * 56, y: ly + Math.sin(t * Math.PI * 4 + rng()) * 2.2 });
    }
    g.lineStyle(1.4, 0x5599ee, 0.45 + rng() * 0.25);
    g.strokePoints(pts);
    // Bright crest on wave
    g.lineStyle(0.7, 0xaad8ff, 0.35 + rng() * 0.20);
    g.strokePoints(pts.map(p => ({ x: p.x, y: p.y - 0.6 })));
  }
}

// ── Hedgehog detail ───────────────────────────────────────────────────────────
// Grassland base with 2–3 Czech hedgehog anti-tank obstacles (crossed steel beams).

function drawHedgehogDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number, col: number, row: number,
): void {
  const rng = makeRng(col + 600, row + 900);
  drawGrasslandDetail(g, cx, cy, col, row);

  const count = 2 + Math.floor(rng() * 2);   // 2 or 3 obstacles
  for (let i = 0; i < count; i++) {
    const ox = cx + (rng() - 0.5) * 28;
    const oy = cy + (rng() - 0.5) * 20;
    const s  = 7 + rng() * 5;   // arm half-length

    // Three steel beams crossing at the centre (X + vertical)
    g.lineStyle(2.8, 0x505050, 1);
    g.lineBetween(ox - s,        oy - s * 0.55, ox + s,        oy + s * 0.55);
    g.lineBetween(ox - s,        oy + s * 0.55, ox + s,        oy - s * 0.55);
    g.lineBetween(ox - s * 0.55, oy - s,        ox + s * 0.55, oy + s);
    // Highlight edge on each beam
    g.lineStyle(0.9, 0x888888, 0.55);
    g.lineBetween(ox - s + 0.5,        oy - s * 0.55 + 0.5, ox + s + 0.5,        oy + s * 0.55 + 0.5);
    g.lineBetween(ox - s + 0.5,        oy + s * 0.55 + 0.5, ox + s + 0.5,        oy - s * 0.55 + 0.5);
    // Centre bolt
    g.fillStyle(0x303030, 1);
    g.fillCircle(ox, oy, 2.2);
    g.fillStyle(0x707070, 0.7);
    g.fillCircle(ox - 0.5, oy - 0.5, 0.9);
  }
}

// ── Bunker detail ─────────────────────────────────────────────────────────────
// Reinforced concrete emplacement.  Slit faces toward the enemy baseline.
// faceSouth logic mirrors sandbags: axis+unflipped → slit faces south.

function drawBunkerDetail(
  g: Phaser.GameObjects.Graphics, cx: number, cy: number,
  tileSide: 'allies' | 'axis' | undefined, flipped: boolean,
): void {
  const r  = HEX_SIZE;
  const bw = r * 0.80;
  const bh = r * 0.58;

  // Shift the whole structure toward the firing slit so units centre in the rear half
  const faceSouth = (tileSide !== 'allies') !== flipped;
  const bcy = cy + (faceSouth ? 16 : -16);

  // Semi-transparent concrete pad so the bunker reads against any terrain
  g.fillStyle(0xa8a090, 0.55);
  g.fillEllipse(cx, bcy, bw * 1.15, bh * 1.35);

  // Outer concrete shell
  g.fillStyle(0x999080, 1);
  g.fillRoundedRect(cx - bw / 2, bcy - bh / 2, bw, bh, 5);
  g.lineStyle(2, 0x4a4840, 0.9);
  g.strokeRoundedRect(cx - bw / 2, bcy - bh / 2, bw, bh, 5);

  // Darker inner roof slab
  g.fillStyle(0x7a7068, 0.85);
  g.fillRoundedRect(cx - bw / 2 + 4, bcy - bh / 2 + 4, bw - 8, bh - 8, 3);

  // Camouflage netting hatching
  g.lineStyle(1, 0x3a5818, 0.30);
  for (let i = -5; i <= 5; i++) {
    const hx = cx + i * 6;
    g.lineBetween(hx - 8, bcy - bh / 2 + 3, hx + 8, bcy + bh / 2 - 3);
  }

  // Firing slit — oriented toward the enemy baseline
  const slitY = bcy + (faceSouth ? bh / 2 - 7 : -bh / 2 + 7);
  g.fillStyle(0x111111, 1);
  g.fillRoundedRect(cx - 10, slitY - 2.5, 20, 5, 1);

  // Corner reinforcement blocks
  g.fillStyle(0x888070, 0.70);
  for (const [sx, sy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
    g.fillRect(cx + sx * (bw / 2 - 6), bcy + sy * (bh / 2 - 5), 7, 6);
  }
}

const TERRAIN_ICON: Partial<Record<TerrainType, string>> = {
  pond: '≈',
};

// Draw base terrain detail (no overlays here).
function drawBaseTerrainDetail(
  g: Phaser.GameObjects.Graphics, type: TerrainType,
  cx: number, cy: number, col: number, row: number, terrain: TerrainHex[],
  terrainTheme?: string,
): void {
  const winter = terrainTheme === 'winter';
  if (type === 'ocean')                               drawOceanDetail(g, cx, cy, col, row);
  else if (type === 'forest' && winter)               drawWinterForestDetail(g, cx, cy, col, row);
  else if (type === 'forest')                         drawForestDetail(g, cx, cy, col, row);
  else if (type === 'town')                           drawTownDetail(g, cx, cy, col, row);
  else if (type === 'hedgerow')                       drawHedgerowDetail(g, cx, cy, col, row);
  else if ((type === 'hill' || type === 'bluff' || type === 'impassable-hill') && winter) { drawWinterHillDetail(g, cx, cy, col, row); if (type === 'impassable-hill') drawImpassableMarker(g, cx, cy); }
  else if (type === 'hill' || type === 'bluff')       drawHillDetail(g, cx, cy, col, row);
  else if (type === 'impassable-hill')                { drawHillDetail(g, cx, cy, col, row); drawImpassableMarker(g, cx, cy); }
  else if (type === 'frozen-lake')                    drawFrozenLakeDetail(g, cx, cy, col, row);
  else if (type === 'river')                          drawRiverDetail(g, cx, cy, col, row, terrain);
  else if (type === 'bridge') {
    const rDirs = riverOnlyEdgeDirs(col, row, terrain);
    if (rDirs.length >= 2) {
      drawRiverDetail(g, cx, cy, col, row, terrain, [rDirs[0], rDirs[1]]);
    } else if (rDirs.length === 1) {
      drawRiverDetail(g, cx, cy, col, row, terrain, [rDirs[0], voidExitDir(col, row, rDirs[0])]);
    }
    drawBridgeOverlay(g, cx, cy, bridgeAngle(col, row, terrain));
  }
  else if (type === 'ravine')          drawRavineDetail(g, cx, cy, col, row, winter);
  else if (type === 'trench')          drawTrenchDetail(g, cx, cy, col, row, winter);
  else if (type === 'city-ruins')      drawCityRuinsDetail(g, cx, cy, col, row, winter);
  else if (type === 'hill-forest')     drawHillForestDetail(g, cx, cy, col, row, winter);
  else if (type === 'hill-village')    drawHillVillageDetail(g, cx, cy, col, row, winter);
  else if (type === 'factory-complex') drawFactoryDetail(g, cx, cy, col, row, winter);
  else if (type === 'marsh')           drawMarshDetail(g, cx, cy, col, row, winter);
  else if (type === 'clear' && winter)  drawSnowClearDetail(g, cx, cy, col, row);
  else drawGrasslandDetail(g, cx, cy, col, row); // clear, beach, pond, etc.
}

// Draw overlay detail on top of the base terrain.
function drawOverlayDetail(
  g: Phaser.GameObjects.Graphics, overlay: OverlayType,
  cx: number, cy: number, col: number, row: number,
  tileSide: 'allies' | 'axis' | undefined, flipped: boolean,
): void {
  if (overlay === 'barbed-wire') {
    drawBarbedWireOverlay(g, cx, cy, col, row);
  } else if (overlay === 'sandbags') {
    // faceSouth = owner is axis XOR board is flipped
    const faceSouth = (tileSide !== 'allies') !== flipped;
    drawSandbagOverlay(g, cx, cy, faceSouth);
  } else if (overlay === 'hedgehog') {
    drawHedgehogDetail(g, cx, cy, col, row);
  } else if (overlay === 'bunker') {
    drawBunkerDetail(g, cx, cy, tileSide, flipped);
  }
}

/**
 * Draws one complete terrain hex (fill, illustration, overlay, border) centred on
 * the Graphics object's local origin — same look as on the board, used for the
 * rules-dialog terrain icons (caller positions/scales the Graphics object itself).
 */
export function drawTerrainTile(
  g: Phaser.GameObjects.Graphics, type: TerrainType, overlay?: OverlayType,
  side?: 'allies' | 'axis', flipped = false, terrainTheme?: string,
): void {
  const pts = hexPoly(0, 0, HEX_SIZE - 1);
  let fillColor = (overlay && OVERLAY_FILL[overlay] !== undefined) ? OVERLAY_FILL[overlay]! : TERRAIN_COLORS[type];
  if (terrainTheme) {
    if (type === 'clear'  && CLEAR_THEME_COLORS[terrainTheme])                    fillColor = CLEAR_THEME_COLORS[terrainTheme];
    if ((type === 'hill' || type === 'bluff' || type === 'impassable-hill' || type === 'ravine' ||
         type === 'trench' || type === 'hill-forest' || type === 'hill-village') &&
        CLEAR_THEME_COLORS[terrainTheme]) fillColor = CLEAR_THEME_COLORS[terrainTheme];
    if (type === 'forest' && FOREST_THEME_COLORS[terrainTheme])                   fillColor = FOREST_THEME_COLORS[terrainTheme];
  }
  g.fillStyle(fillColor, 1);
  g.fillPoints(pts, true);
  drawBaseTerrainDetail(g, type, 0, 0, 0, 0, [], terrainTheme);
  if (overlay) drawOverlayDetail(g, overlay, 0, 0, 0, 0, side, flipped);
  g.lineStyle(1, 0x333333, 0.7);
  g.strokePoints(pts, true);
}

// ── Medal of Honor badge ──────────────────────────────────────────────────────
// Drawn on objective hexes as a floating badge (under unit sprites).

export function drawMedalBadge(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const R = 13;

  // Outer gold ring
  g.fillStyle(0xc8960c, 1);
  g.fillCircle(cx, cy, R);
  g.lineStyle(1.2, 0xf8d848, 0.75);
  g.strokeCircle(cx, cy, R - 0.8);

  // Blue interior
  g.fillStyle(0x3a78c8, 1);
  g.fillCircle(cx, cy, R - 2.5);

  // Upper sheen
  g.fillStyle(0x90c0ff, 0.30);
  g.fillEllipse(cx, cy - 2, 18, 11);

  // 5-pointed gold star
  const oR = 7.5, iR = 3.0;
  const star: { x: number; y: number }[] = [];
  for (let i = 0; i < 10; i++) {
    const a = (i * Math.PI / 5) - Math.PI / 2;
    star.push({ x: cx + (i % 2 === 0 ? oR : iR) * Math.cos(a),
                y: cy + (i % 2 === 0 ? oR : iR) * Math.sin(a) });
  }
  g.fillStyle(0xeccc28, 1);
  g.fillPoints(star, true);
  g.lineStyle(0.8, 0x9a7c10, 0.80);
  g.strokePoints(star, true);

  // Central emblem disc
  g.fillStyle(0xb09020, 1);
  g.fillCircle(cx, cy, 2.6);
  g.fillStyle(0xfce870, 0.55);
  g.fillCircle(cx - 0.7, cy - 0.7, 1.0);

  // Eagle — simplified wings + head at the top of the ring
  g.fillStyle(0xd4a820, 1);
  g.fillTriangle(cx,     cy -  9, cx - 5, cy - 12.5, cx - 2, cy - 8); // left wing
  g.fillTriangle(cx,     cy -  9, cx + 5, cy - 12.5, cx + 2, cy - 8); // right wing
  g.fillCircle(cx, cy - 11, 2.0);                                       // head
  g.fillStyle(0xf8d040, 0.70);
  g.fillCircle(cx, cy - 12, 0.9);                                       // beak glint
}

// ── HexBoard ──────────────────────────────────────────────────────────────────

// Exported so UnitSprite can position itself in the correct visual space.
export function hexToPixelFlipped(
  col: number, row: number, offsetX: number, offsetY: number, flipped: boolean,
): { x: number; y: number } {
  if (!flipped) return hexToPixel(col, row, offsetX, offsetY);
  return hexToPixel(maxColInRow(row) - col, (BOARD_ROWS - 1) - row, offsetX, offsetY);
}

export class HexBoard extends Phaser.GameObjects.Container {
  private baseFg:       Phaser.GameObjects.Graphics;
  private occupiedFg:   Phaser.GameObjects.Graphics;
  private highlightFg:  Phaser.GameObjects.Graphics;
  private diceHintGfx:  Phaser.GameObjects.Graphics;
  private reviewGfx:    Phaser.GameObjects.Graphics;
  private diceHintTexts: Phaser.GameObjects.Text[] = [];
  private terrainIconTexts: Phaser.GameObjects.Text[] = [];
  private reviewTexts: Phaser.GameObjects.Text[] = [];
  /** Set true during a multi-touch gesture (pinch/pan) to suppress hex clicks. */
  public gestureActive = false;

  /** Controls whether the hover terrain tooltip is shown (desktop only). */
  public tooltipEnabled = true;

  private offsetX: number;
  private offsetY: number;
  private flipped  = false;
  private hexLabels:   Map<string, string> = new Map();
  private tooltip:     Phaser.GameObjects.Text | null = null;
  private terrain:     TerrainHex[] = [];

  private toVisual(col: number, row: number): HexCoord {
    if (!this.flipped) return { col, row };
    return { col: maxColInRow(row) - col, row: (BOARD_ROWS - 1) - row };
  }

  constructor(scene: Phaser.Scene, offsetX: number, offsetY: number) {
    super(scene, 0, 0);
    this.offsetX = offsetX;
    this.offsetY = offsetY;

    this.baseFg      = scene.add.graphics();
    this.occupiedFg  = scene.add.graphics();
    this.highlightFg = scene.add.graphics();
    this.diceHintGfx = scene.add.graphics().setDepth(4);
    this.reviewGfx   = scene.add.graphics().setDepth(3);

    // Use pointerup (not pointerdown) so we can check gestureActive and pointer travel
    // distance AFTER the full touch gesture resolves — prevents pan from triggering hex clicks.
    scene.input.on('pointerup', (ptr: Phaser.Input.Pointer) => {
      if (this.gestureActive) return;           // pan/zoom in progress — suppress
      if (ptr.getDistance() > 10) return;       // finger moved too far — it was a drag
      if (scene.input.hitTestPointer(ptr).length > 0) return;
      const vhex = pixelToHex(ptr.worldX, ptr.worldY, this.offsetX, this.offsetY);
      if (!vhex) return;
      const hex = this.flipped
        ? { col: maxColInRow(vhex.row) - vhex.col, row: (BOARD_ROWS - 1) - vhex.row }
        : vhex;
      this.emit('hexClick', hex);
    });

    // Hover tooltip — show location name + terrain summary (desktop only; touch has no hover)
    if (!scene.sys.game.device.input.touch) {
      scene.input.on('pointermove', (ptr: Phaser.Input.Pointer) => {
        if (!this.tooltipEnabled) { this.tooltip?.setVisible(false); return; }
        const visual = pixelToHex(ptr.worldX, ptr.worldY, this.offsetX, this.offsetY);
        if (!visual) { this.tooltip?.setVisible(false); return; }

        // Convert visual hex to game-space coordinates (board is flipped for axis player)
        const hex = this.flipped
          ? { col: maxColInRow(visual.row) - visual.col, row: (BOARD_ROWS - 1) - visual.row }
          : visual;

        const label   = this.hexLabels.get(`${hex.col},${hex.row}`);
        const terrain = this.terrain.find(t => t.col === hex.col && t.row === hex.row);
        const summary = terrain ? getTerrainSummary(terrain.type, terrain.overlay, terrain.permanent) : null;
        const mine    = terrain?.minefield
          ? `Minefield (${terrain.minefield.ownerSide}) — strength ${terrain.minefield.revealed ? terrain.minefield.strength : 'unknown'}`
          : null;

        const text = [label, summary, mine].filter(Boolean).join('\n──────────────\n');
        if (text) {
          if (!this.tooltip) {
            this.tooltip = scene.add.text(0, 0, '', {
              fontSize: '11px', color: '#ffffff',
              backgroundColor: '#000000dd',
              padding: { x: 8, y: 6 },
              lineSpacing: 3,
            }).setDepth(200);
          }
          this.tooltip.setText(text);
          this.tooltip.setPosition(ptr.x + 14, ptr.y - 28);
          this.tooltip.setVisible(true);
        } else {
          this.tooltip?.setVisible(false);
        }
      });
    }
  }

  /** Reparent this board and its scene-root graphics layers into a fixed
   *  board-camera layer (mobile camera split). */
  addLayerObjectsTo(layer: Phaser.GameObjects.Container): void {
    layer.add([this, this.baseFg, this.occupiedFg, this.highlightFg, this.diceHintGfx, this.reviewGfx]);
  }

  render(terrain: TerrainHex[], specialHexes: SpecialHex[], flipped = false, terrainTheme?: string): void {
    this.terrain = terrain; // store for hover tooltip lookup
    this.flipped = flipped;
    this.baseFg.clear();
    this.hexLabels.clear();
    this.terrainIconTexts.forEach(t => t.destroy());
    this.terrainIconTexts = [];

    // For Axis, rotate the board 180°: transform all game coords into visual coords.
    // River neighbour lookups and RNG are then done in visual space, which is correct
    // because the standard odd-r offset adjacency is preserved under 180° rotation.
    const visTerrain: TerrainHex[] = flipped
      ? terrain.map(h => ({ ...h, col: maxColInRow(h.row) - h.col, row: (BOARD_ROWS - 1) - h.row }))
      : terrain;
    const visSpecial: SpecialHex[] = flipped
      ? specialHexes.map(sh => ({ ...sh, col: maxColInRow(sh.row) - sh.col, row: (BOARD_ROWS - 1) - sh.row }))
      : specialHexes;

    for (let row = 0; row < BOARD_ROWS; row++) {
      for (let col = 0; col <= maxColInRow(row); col++) {
        const t       = visTerrain.find(h => h.col === col && h.row === row);
        const type    = t?.type ?? 'clear';
        const overlay = t?.overlay;
        const { x, y } = hexToPixel(col, row, this.offsetX, this.offsetY);
        const pts = hexPoly(x, y, HEX_SIZE - 1);

        // 1. hex fill — bunker overlay uses concrete colour; theme overrides clear/hill/forest base colour
        const themeColor        = terrainTheme && CLEAR_THEME_COLORS[terrainTheme];
        const forestThemeColor  = terrainTheme && FOREST_THEME_COLORS[terrainTheme];
        const fillColor = (overlay && OVERLAY_FILL[overlay] !== undefined)
          ? OVERLAY_FILL[overlay]!
          : (forestThemeColor && type === 'forest')
            ? forestThemeColor
            : (themeColor && (type === 'clear' || type === 'hill' || type === 'bluff' || type === 'impassable-hill' ||
                type === 'ravine' || type === 'trench' || type === 'hill-forest' || type === 'hill-village'))
              ? themeColor
              : TERRAIN_COLORS[type];
        this.baseFg.fillStyle(fillColor, 1);
        this.baseFg.fillPoints(pts, true);

        // 2. base terrain illustration
        drawBaseTerrainDetail(this.baseFg, type, x, y, col, row, visTerrain, terrainTheme);

        // 3. overlay illustration on top
        if (overlay) {
          drawOverlayDetail(this.baseFg, overlay, x, y, col, row, t?.side, flipped);
        }

        // 4. hex border
        this.baseFg.lineStyle(1, 0x333333, 0.7);
        this.baseFg.strokePoints(pts, true);

        // text icon for base terrain types that use one
        const icon = t ? TERRAIN_ICON[type] : undefined;
        if (icon) {
          const iconText = this.scene.add.text(x, y, icon, {
            fontSize: '10px', color: '#000000', fontStyle: 'bold',
          }).setOrigin(0.5);
          this.add(iconText);
          this.terrainIconTexts.push(iconText);
        }

        // Minefield marker — presence/location/owner are public info (physical piece sits
        // face-up); the strength number itself renders as "?" until revealed by a trigger.
        if (t?.minefield) {
          const markerColor = t.minefield.ownerSide === 'allies' ? 0x4488cc : 0xcc4444;
          this.baseFg.fillStyle(0x1a1a1a, 0.85);
          this.baseFg.fillCircle(x, y + 14, 7);
          this.baseFg.lineStyle(1.5, markerColor, 0.95);
          this.baseFg.strokeCircle(x, y + 14, 7);
          const mineText = this.scene.add.text(x, y + 14, t.minefield.revealed ? `${t.minefield.strength}` : '?', {
            fontSize: '9px', color: '#ffcc00', fontStyle: 'bold',
          }).setOrigin(0.5);
          this.add(mineText);
          this.terrainIconTexts.push(mineText);
        }
      }
    }

    for (const sh of visSpecial) {
      if (sh.label) this.hexLabels.set(`${sh.col},${sh.row}`, sh.label);
      if (sh.medal && !sh.hideBadge) {
        const { x, y } = hexToPixel(sh.col, sh.row, this.offsetX, this.offsetY);
        if (sh.medal === 'allies') drawMedalBadge(this.baseFg, x, y);
        else                       drawIronCross(this.baseFg, x, y, 13);
      }
    }

    this.drawSectionLines();
  }

  private drawSectionLines(): void {
    const topY    = this.offsetY - HEX_SIZE;
    const bottomY = this.offsetY + V_SPACING * (BOARD_ROWS - 1) + HEX_SIZE;
    const DASH = 6, GAP = 5;

    for (const borderX of [
      this.offsetX + H_SPACING * 3.5,
      this.offsetX + H_SPACING * 8.5,
    ]) {
      this.baseFg.lineStyle(1.5, 0xcc2222, 0.80);
      let y = topY;
      while (y < bottomY) {
        this.baseFg.lineBetween(borderX, y, borderX, Math.min(y + DASH, bottomY));
        y += DASH + GAP;
      }
    }
  }

  highlightHexes(hexes: HexCoord[], color: number, alpha = 0.35): void {
    for (const h of hexes) {
      const v = this.toVisual(h.col, h.row);
      const { x, y } = hexToPixel(v.col, v.row, this.offsetX, this.offsetY);
      this.highlightFg.fillStyle(color, alpha);
      this.highlightFg.fillPoints(hexPoly(x, y, HEX_SIZE - 2), true);
    }
  }

  /** Cyan circle + crosshair on highlightFg — used to mark newly airdropped units. */
  markAirdropHex(col: number, row: number): void {
    const v = this.toVisual(col, row);
    const { x, y } = hexToPixel(v.col, v.row, this.offsetX, this.offsetY);
    this.highlightFg.lineStyle(2.5, 0x88eeff, 0.9);
    this.highlightFg.strokeCircle(x, y, 18);
    this.highlightFg.fillStyle(0x88eeff, 0.2);
    this.highlightFg.fillCircle(x, y, 18);
    this.highlightFg.lineStyle(2, 0x88eeff, 0.9);
    this.highlightFg.strokeLineShape(new Phaser.Geom.Line(x - 8, y, x + 8, y));
    this.highlightFg.strokeLineShape(new Phaser.Geom.Line(x, y - 8, x, y + 8));
  }

  outlineHex(col: number, row: number, color: number): void {
    const v = this.toVisual(col, row);
    const { x, y } = hexToPixel(v.col, v.row, this.offsetX, this.offsetY);
    this.highlightFg.lineStyle(3, color, 1);
    this.highlightFg.strokePoints(hexPoly(x, y, HEX_SIZE - 1), true);
  }

  /** Draw an orange outline + directional arrows on the attacker's view during a pending retreat. */
  showRetreatArrows(unitCol: number, unitRow: number, validHexes: HexCoord[]): void {
    this.outlineHex(unitCol, unitRow, 0xffaa00);

    const uv = this.toVisual(unitCol, unitRow);
    const { x: ux, y: uy } = hexToPixel(uv.col, uv.row, this.offsetX, this.offsetY);

    this.highlightFg.fillStyle(0xffaa00, 1);
    this.highlightFg.lineStyle(2.5, 0xffaa00, 1);

    for (const hex of validHexes) {
      const tv = this.toVisual(hex.col, hex.row);
      const { x: tx, y: ty } = hexToPixel(tv.col, tv.row, this.offsetX, this.offsetY);

      const dx = tx - ux;
      const dy = ty - uy;
      const len = Math.sqrt(dx * dx + dy * dy);
      const ndx = dx / len;
      const ndy = dy / len;

      // Shaft: start 14px from unit centre, end 52% of the way to target
      const sx = ux + ndx * 14;
      const sy = uy + ndy * 14;
      const ex = ux + ndx * (len * 0.52);
      const ey = uy + ndy * (len * 0.52);

      this.highlightFg.strokeLineShape(new Phaser.Geom.Line(sx, sy, ex, ey));

      // Arrowhead triangle
      const aw = 7;
      this.highlightFg.fillTriangle(
        ex, ey,
        ex - ndx * aw + ndy * aw * 0.6,
        ey - ndy * aw - ndx * aw * 0.6,
        ex - ndx * aw - ndy * aw * 0.6,
        ey - ndy * aw + ndx * aw * 0.6,
      );
    }
  }

  showDiceHint(col: number, row: number, count: number): void {
    const v = this.toVisual(col, row);
    const { x, y } = hexToPixel(v.col, v.row, this.offsetX, this.offsetY);
    const bx = x;
    const by = y - 26;   // top area of hex, above unit figures
    const bw = 18, bh = 18, r = 3;

    // Die face background — cream square with dark border
    this.diceHintGfx.fillStyle(0xf5f0d0, 1);
    this.diceHintGfx.fillRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, r);
    this.diceHintGfx.lineStyle(1.5, 0x222222, 1);
    this.diceHintGfx.strokeRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, r);

    // Number label
    const label = this.scene.add.text(bx, by, `${count}`, {
      fontSize: '11px', color: '#111111', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(5);
    this.add(label);
    this.diceHintTexts.push(label);
  }

  /** Draw a subtle side-tinted overlay on every hex that has a live unit, improving contrast. */
  setOccupiedHexes(units: { col: number; row: number; side: string }[]): void {
    this.occupiedFg.clear();
    for (const u of units) {
      const { col, row } = this.toVisual(u.col, u.row);
      const { x, y } = hexToPixel(col, row, this.offsetX, this.offsetY);
      this.occupiedFg.fillStyle(u.side === 'allies' ? 0x99aaff : 0xff9999, 0.30);
      this.occupiedFg.fillPoints(hexPoly(x, y, HEX_SIZE - 1), true);
    }
  }

  clearHighlights(): void {
    this.highlightFg.clear();
    this.diceHintGfx.clear();
    this.diceHintTexts.forEach(t => t.destroy());
    this.diceHintTexts = [];
  }

  /** Draw move (yellow), attack (red) arrows and airdrop (cyan) markers for a set of log entries. */
  showReviewOverlay(entries: LogEntry[]): void {
    this.reviewGfx.clear();
    for (const e of entries) {
      if (e.type === 'move' && e.fromCol !== undefined && e.fromRow !== undefined && e.toCol !== undefined && e.toRow !== undefined) {
        this.drawReviewArrow(e.fromCol, e.fromRow, e.toCol, e.toRow, 0xffdd00, 0.9);
      } else if (e.type === 'attack' && e.fromCol !== undefined && e.fromRow !== undefined && e.targetCol !== undefined && e.targetRow !== undefined) {
        this.drawReviewArrow(e.fromCol, e.fromRow, e.targetCol, e.targetRow, 0xff3333, 1);
      } else if (e.type === 'retreat' && e.fromCol !== undefined && e.fromRow !== undefined && e.toCol !== undefined && e.toRow !== undefined) {
        this.drawReviewArrow(e.fromCol, e.fromRow, e.toCol, e.toRow, 0xaa66ff, 0.9);
      } else if (e.type === 'eliminate' && e.toCol !== undefined && e.toRow !== undefined) {
        const v = this.toVisual(e.toCol, e.toRow);
        const { x, y } = hexToPixel(v.col, v.row, this.offsetX, this.offsetY);
        const skull = this.scene.add.text(x, y, '💀', { fontSize: '22px' }).setOrigin(0.5).setDepth(4);
        this.add(skull);
        this.reviewTexts.push(skull);
      } else if (e.type === 'attack' && e.targetCol !== undefined && e.targetRow !== undefined && e.fromCol === undefined) {
        // Special attacks with no attacker unit: Air Power (orange) or Barrage (red)
        const isAir = e.text.startsWith('Air Power');
        const color = isAir ? 0xff7700 : 0xff2222;
        const tv = this.toVisual(e.targetCol, e.targetRow);
        const { x: tx, y: ty } = hexToPixel(tv.col, tv.row, this.offsetX, this.offsetY);
        this.reviewGfx.lineStyle(3, color, 1);
        this.reviewGfx.strokeCircle(tx, ty, 20);
        this.reviewGfx.fillStyle(color, 0.18);
        this.reviewGfx.fillCircle(tx, ty, 20);
        // Crosshair
        this.reviewGfx.lineStyle(2, color, 0.9);
        this.reviewGfx.strokeLineShape(new Phaser.Geom.Line(tx - 13, ty, tx + 13, ty));
        this.reviewGfx.strokeLineShape(new Phaser.Geom.Line(tx, ty - 13, tx, ty + 13));
        const icon = this.scene.add.text(tx, ty - 28, isAir ? '✈' : '💣', { fontSize: '13px' })
          .setOrigin(0.5).setDepth(4);
        this.add(icon);
        this.reviewTexts.push(icon);
      } else if (e.type === 'order' && e.toCol !== undefined && e.toRow !== undefined && e.text.startsWith('Medics')) {
        // Medics healing — green medical cross on healed unit hex
        const mv = this.toVisual(e.toCol, e.toRow);
        const { x: mx, y: my } = hexToPixel(mv.col, mv.row, this.offsetX, this.offsetY);
        this.reviewGfx.lineStyle(2.5, 0x44ff88, 0.9);
        this.reviewGfx.strokeCircle(mx, my, 18);
        this.reviewGfx.fillStyle(0x44ff88, 0.18);
        this.reviewGfx.fillCircle(mx, my, 18);
        // Medical cross
        this.reviewGfx.lineStyle(3, 0x44ff88, 1);
        this.reviewGfx.strokeLineShape(new Phaser.Geom.Line(mx - 10, my, mx + 10, my));
        this.reviewGfx.strokeLineShape(new Phaser.Geom.Line(mx, my - 10, mx, my + 10));
        const medIcon = this.scene.add.text(mx, my - 26, '🩹', { fontSize: '13px' })
          .setOrigin(0.5).setDepth(4);
        this.add(medIcon);
        this.reviewTexts.push(medIcon);
      } else if (e.type === 'airdrop' && e.toCol !== undefined && e.toRow !== undefined) {
        const v = this.toVisual(e.toCol, e.toRow);
        const { x, y } = hexToPixel(v.col, v.row, this.offsetX, this.offsetY);
        this.reviewGfx.lineStyle(2.5, 0x88eeff, 0.9);
        this.reviewGfx.strokeCircle(x, y, 18);
        this.reviewGfx.fillStyle(0x88eeff, 0.25);
        this.reviewGfx.fillCircle(x, y, 18);
        // Small cross in the centre
        this.reviewGfx.lineStyle(2, 0x88eeff, 0.9);
        this.reviewGfx.strokeLineShape(new Phaser.Geom.Line(x - 8, y, x + 8, y));
        this.reviewGfx.strokeLineShape(new Phaser.Geom.Line(x, y - 8, x, y + 8));
      }
    }
  }

  clearReviewOverlay(): void {
    this.reviewGfx.clear();
    this.reviewTexts.forEach(t => t.destroy());
    this.reviewTexts = [];
  }

  private drawReviewArrow(
    fromCol: number, fromRow: number, toCol: number, toRow: number,
    color: number, alpha: number,
  ): void {
    const fv = this.toVisual(fromCol, fromRow);
    const tv = this.toVisual(toCol, toRow);
    const { x: fx, y: fy } = hexToPixel(fv.col, fv.row, this.offsetX, this.offsetY);
    const { x: tx, y: ty } = hexToPixel(tv.col, tv.row, this.offsetX, this.offsetY);

    const dx  = tx - fx;
    const dy  = ty - fy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) return;
    const ndx = dx / len;
    const ndy = dy / len;

    // Shaft from 16px out of origin to 16px before tip
    const sx = fx + ndx * 16;
    const sy = fy + ndy * 16;
    const ex = tx - ndx * 16;
    const ey = ty - ndy * 16;

    this.reviewGfx.lineStyle(2.5, color, alpha);
    this.reviewGfx.strokeLineShape(new Phaser.Geom.Line(sx, sy, ex, ey));

    // Arrowhead
    const aw = 8;
    this.reviewGfx.fillStyle(color, alpha);
    this.reviewGfx.fillTriangle(
      ex + ndx * aw, ey + ndy * aw,
      ex - ndx * aw + ndy * aw * 0.65, ey - ndy * aw - ndx * aw * 0.65,
      ex - ndx * aw - ndy * aw * 0.65, ey - ndy * aw + ndx * aw * 0.65,
    );
  }
}
